import { useEffect, useState, type ClipboardEvent, type FormEvent, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';

import type { EmailServerInput, EmailServerViewModel } from '../api';

interface Draft {
  name: string;
  region: string;
  fromAddress: string;
  fromName: string;
  replyTo: string;
  configurationSet: string;
  accessKeyId: string;
  secretAccessKey: string;
  isDefault: boolean;
  active: boolean;
  snsTopicArnAllowlist: string;
  feedbackQueueUrl: string;
  feedbackQueueRegion: string;
}

const EMPTY_DRAFT: Draft = {
  name: '',
  region: '',
  fromAddress: '',
  fromName: '',
  replyTo: '',
  configurationSet: '',
  accessKeyId: '',
  secretAccessKey: '',
  isDefault: false,
  active: true,
  snsTopicArnAllowlist: '',
  feedbackQueueUrl: '',
  feedbackQueueRegion: '',
};

function draftFrom(row: EmailServerViewModel): Draft {
  return {
    name: row.name,
    region: row.region,
    fromAddress: row.fromAddress,
    fromName: row.fromName,
    replyTo: row.replyTo,
    configurationSet: row.configurationSet,
    accessKeyId: row.accessKeyId,
    secretAccessKey: '',
    isDefault: row.isDefault,
    active: row.active,
    snsTopicArnAllowlist: row.snsTopicArnAllowlist,
    feedbackQueueUrl: row.feedbackQueueUrl,
    feedbackQueueRegion: row.feedbackQueueRegion,
  };
}

/**
 * Maps each editable draft field to its key in the clipboard JSON (snake_case — the same shape the
 * API uses, so an exported config reads like the wire DTO). Read-only fields (id, has_secret) are
 * intentionally excluded.
 */
type ClipboardKind = 'string' | 'boolean';
interface ClipboardField {
  readonly json: string;
  readonly draft: keyof Draft;
  readonly kind: ClipboardKind;
}

const CLIPBOARD_FIELDS: readonly ClipboardField[] = [
  { json: 'name', draft: 'name', kind: 'string' },
  { json: 'region', draft: 'region', kind: 'string' },
  { json: 'from_address', draft: 'fromAddress', kind: 'string' },
  { json: 'from_name', draft: 'fromName', kind: 'string' },
  { json: 'reply_to', draft: 'replyTo', kind: 'string' },
  { json: 'configuration_set', draft: 'configurationSet', kind: 'string' },
  { json: 'access_key_id', draft: 'accessKeyId', kind: 'string' },
  { json: 'secret_access_key', draft: 'secretAccessKey', kind: 'string' },
  { json: 'sns_topic_arn_allowlist', draft: 'snsTopicArnAllowlist', kind: 'string' },
  { json: 'feedback_queue_url', draft: 'feedbackQueueUrl', kind: 'string' },
  { json: 'feedback_queue_region', draft: 'feedbackQueueRegion', kind: 'string' },
  { json: 'is_default', draft: 'isDefault', kind: 'boolean' },
  { json: 'active', draft: 'active', kind: 'boolean' },
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Serialise the editable draft fields to a plain JSON object (snake_case). */
function draftToJson(draft: Draft): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  for (const field of CLIPBOARD_FIELDS) {
    out[field.json] = draft[field.draft];
  }
  return out;
}

/** Apply a parsed clipboard object onto the draft. Accepts both snake_case and the draft key. */
function applyJsonToDraft(prev: Draft, parsed: Record<string, unknown>): { draft: Draft; applied: number } {
  const next = { ...prev } as Record<keyof Draft, string | boolean>;
  let applied = 0;
  for (const field of CLIPBOARD_FIELDS) {
    const raw = field.json in parsed ? parsed[field.json] : parsed[field.draft];
    if (raw === undefined) continue;
    if (field.kind === 'boolean') {
      if (typeof raw === 'boolean') {
        next[field.draft] = raw;
        applied += 1;
      }
    } else if (typeof raw === 'string') {
      next[field.draft] = raw;
      applied += 1;
    }
  }
  return { draft: next as unknown as Draft, applied };
}

interface FieldErrors {
  name?: string;
  region?: string;
  fromAddress?: string;
  accessKeyId?: string;
  secretAccessKey?: string;
}

type ClipboardNote = { readonly kind: 'ok' | 'error'; readonly text: string } | null;

export function EmailServerFormDialog({
  open,
  initial,
  onOpenChange,
  onSubmit,
  pending,
  submitError,
}: {
  readonly open: boolean;
  /** Non-null = edit; null = create. */
  readonly initial: EmailServerViewModel | null;
  readonly onOpenChange: (next: boolean) => void;
  readonly onSubmit: (input: EmailServerInput) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [copied, setCopied] = useState(false);
  const [clipboardNote, setClipboardNote] = useState<ClipboardNote>(null);
  const isEdit = initial !== null;

  useEffect(() => {
    if (open) {
      setDraft(initial !== null ? draftFrom(initial) : EMPTY_DRAFT);
      setErrors({});
      setCopied(false);
      setClipboardNote(null);
    }
  }, [open, initial]);

  function set<K extends keyof Draft>(key: K, value: Draft[K]): void {
    setDraft((p) => ({ ...p, [key]: value }));
  }

  async function handleCopy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(JSON.stringify(draftToJson(draft), null, 2));
      setCopied(true);
      setClipboardNote(null);
      window.setTimeout(() => {
        setCopied(false);
      }, 1500);
    } catch {
      setClipboardNote({ kind: 'error', text: t('routes.admin.email.servers.form.clipboard.copyError') });
    }
  }

  /**
   * Import on a real paste gesture (Ctrl/Cmd+V) — the paste event carries the clipboard text
   * directly, so there's no browser permission prompt. We only hijack the paste when it parses to
   * one of our exported configs; otherwise it falls through as a normal paste into the focused field.
   */
  function handlePaste(event: ClipboardEvent<HTMLFormElement>): void {
    const text = event.clipboardData.getData('text');
    if (text.trim().length === 0) return;
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return;
    }
    if (!isRecord(parsed)) return;
    const { draft: nextDraft, applied } = applyJsonToDraft(draft, parsed);
    if (applied === 0) return;
    event.preventDefault();
    setDraft(nextDraft);
    setErrors({});
    setClipboardNote({
      kind: 'ok',
      text: t('routes.admin.email.servers.form.clipboard.imported', { count: applied }),
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: FieldErrors = {};
    if (draft.name.trim().length === 0) next.name = t('routes.admin.email.servers.form.errors.nameRequired');
    if (draft.region.trim().length === 0) next.region = t('routes.admin.email.servers.form.errors.regionRequired');
    if (draft.fromAddress.trim().length === 0)
      next.fromAddress = t('routes.admin.email.servers.form.errors.fromAddressRequired');
    if (draft.accessKeyId.trim().length === 0)
      next.accessKeyId = t('routes.admin.email.servers.form.errors.accessKeyIdRequired');
    const secretRequired = !(initial?.hasSecret ?? false);
    if (secretRequired && draft.secretAccessKey.trim().length === 0)
      next.secretAccessKey = t('routes.admin.email.servers.form.errors.secretRequired');
    if (Object.keys(next).length > 0) {
      setErrors(next);
      return;
    }
    setErrors({});
    onSubmit({
      name: draft.name,
      region: draft.region,
      fromAddress: draft.fromAddress,
      fromName: draft.fromName,
      replyTo: draft.replyTo,
      configurationSet: draft.configurationSet,
      accessKeyId: draft.accessKeyId,
      secretAccessKey: draft.secretAccessKey,
      isDefault: draft.isDefault,
      active: draft.active,
      snsTopicArnAllowlist: draft.snsTopicArnAllowlist,
      feedbackQueueUrl: draft.feedbackQueueUrl,
      feedbackQueueRegion: draft.feedbackQueueRegion,
    });
  }

  const fieldPrefix = 'routes.admin.email.servers.form.fields';
  const hintPrefix = 'routes.admin.email.servers.form.hints';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('routes.admin.email.servers.edit.title')
              : t('routes.admin.email.servers.create.title')}
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={handleSubmit}
          onPaste={handlePaste}
          noValidate
          className="space-y-4 max-h-[70vh] overflow-y-auto pr-1"
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <FormField
              id="email-server-name"
              label={t(`${fieldPrefix}.name`)}
              hint={t(`${hintPrefix}.name`)}
              required
              error={errors.name}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.name}
                  onChange={(e) => {
                    set('name', e.target.value);
                  }}
                  autoComplete="off"
                />
              )}
            </FormField>
            <FormField
              id="email-server-region"
              label={t(`${fieldPrefix}.region`)}
              hint={t(`${hintPrefix}.region`)}
              required
              error={errors.region}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.region}
                  onChange={(e) => {
                    set('region', e.target.value);
                  }}
                  placeholder="us-east-1"
                  autoComplete="off"
                  className="font-mono text-sm"
                />
              )}
            </FormField>
            <FormField
              id="email-server-from-address"
              label={t(`${fieldPrefix}.fromAddress`)}
              hint={t(`${hintPrefix}.fromAddress`)}
              required
              error={errors.fromAddress}
            >
              {(fp) => (
                <Input
                  {...fp}
                  type="email"
                  value={draft.fromAddress}
                  onChange={(e) => {
                    set('fromAddress', e.target.value);
                  }}
                  placeholder="no-reply@clinic.example"
                  autoComplete="off"
                />
              )}
            </FormField>
            <FormField
              id="email-server-from-name"
              label={t(`${fieldPrefix}.fromName`)}
              hint={t(`${hintPrefix}.fromName`)}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.fromName}
                  onChange={(e) => {
                    set('fromName', e.target.value);
                  }}
                  autoComplete="off"
                />
              )}
            </FormField>
            <FormField
              id="email-server-reply-to"
              label={t(`${fieldPrefix}.replyTo`)}
              hint={t(`${hintPrefix}.replyTo`)}
            >
              {(fp) => (
                <Input
                  {...fp}
                  type="email"
                  value={draft.replyTo}
                  onChange={(e) => {
                    set('replyTo', e.target.value);
                  }}
                  autoComplete="off"
                />
              )}
            </FormField>

            <FormField
              id="email-server-access-key-id"
              label={t(`${fieldPrefix}.accessKeyId`)}
              hint={t(`${hintPrefix}.accessKeyId`)}
              required
              error={errors.accessKeyId}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.accessKeyId}
                  onChange={(e) => {
                    set('accessKeyId', e.target.value);
                  }}
                  autoComplete="off"
                  className="font-mono text-sm"
                />
              )}
            </FormField>
            <FormField
              id="email-server-secret-access-key"
              label={t(`${fieldPrefix}.secretAccessKey`)}
              hint={t(`${hintPrefix}.secretAccessKey`)}
              error={errors.secretAccessKey}
            >
              {(fp) => (
                <Input
                  {...fp}
                  type="password"
                  value={draft.secretAccessKey}
                  onChange={(e) => {
                    set('secretAccessKey', e.target.value);
                  }}
                  autoComplete="off"
                  placeholder={
                    (initial?.hasSecret ?? false)
                      ? t('routes.admin.email.servers.form.secretKeepPlaceholder')
                      : undefined
                  }
                />
              )}
            </FormField>

            <FormField
              id="email-server-configuration-set"
              label={t(`${fieldPrefix}.configurationSet`)}
              hint={t(`${hintPrefix}.configurationSet`)}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.configurationSet}
                  onChange={(e) => {
                    set('configurationSet', e.target.value);
                  }}
                  autoComplete="off"
                />
              )}
            </FormField>

            <FormField
              id="email-server-sns-allowlist"
              label={t(`${fieldPrefix}.snsTopicArnAllowlist`)}
              hint={t(`${hintPrefix}.snsTopicArnAllowlist`)}
              className="sm:col-span-3"
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.snsTopicArnAllowlist}
                  onChange={(e) => {
                    set('snsTopicArnAllowlist', e.target.value);
                  }}
                  autoComplete="off"
                  className="font-mono text-xs"
                />
              )}
            </FormField>

            <FormField
              id="email-server-feedback-queue-url"
              label={t(`${fieldPrefix}.feedbackQueueUrl`)}
              hint={t(`${hintPrefix}.feedbackQueueUrl`)}
              className="sm:col-span-2"
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.feedbackQueueUrl}
                  onChange={(e) => {
                    set('feedbackQueueUrl', e.target.value);
                  }}
                  autoComplete="off"
                  className="font-mono text-xs"
                />
              )}
            </FormField>
            <FormField
              id="email-server-feedback-queue-region"
              label={t(`${fieldPrefix}.feedbackQueueRegion`)}
              hint={t(`${hintPrefix}.feedbackQueueRegion`)}
            >
              {(fp) => (
                <Input
                  {...fp}
                  value={draft.feedbackQueueRegion}
                  onChange={(e) => {
                    set('feedbackQueueRegion', e.target.value);
                  }}
                  placeholder="us-east-1"
                  autoComplete="off"
                  className="font-mono text-sm"
                />
              )}
            </FormField>
          </div>

          <div className="flex items-center gap-6">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.isDefault}
                onChange={(e) => {
                  set('isDefault', e.target.checked);
                }}
                className="h-3.5 w-3.5"
              />
              {t(`${fieldPrefix}.isDefault`)}
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={draft.active}
                onChange={(e) => {
                  set('active', e.target.checked);
                }}
                className="h-3.5 w-3.5"
              />
              {t(`${fieldPrefix}.active`)}
            </label>
          </div>

          {submitError !== undefined && submitError !== null && (
            <p role="alert" className="text-sm text-red-600">
              {t('routes.admin.email.servers.form.errors.submit', { code: getErrorCodeForUi(submitError) })}
            </p>
          )}

          <DialogFooter className="sm:items-center sm:justify-between sm:space-x-0">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  void handleCopy();
                }}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied
                  ? t('routes.admin.email.servers.form.clipboard.copied')
                  : t('routes.admin.email.servers.form.clipboard.copy')}
              </Button>
              {clipboardNote !== null ? (
                <span
                  role={clipboardNote.kind === 'error' ? 'alert' : undefined}
                  className={
                    clipboardNote.kind === 'error' ? 'text-xs text-red-600' : 'text-xs text-muted-foreground'
                  }
                >
                  {clipboardNote.text}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {t('routes.admin.email.servers.form.clipboard.pasteHint')}
                </span>
              )}
            </div>
            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                }}
                disabled={pending}
              >
                {t('routes.admin.email.servers.form.cancel')}
              </Button>
              <Button type="submit" loading={pending}>
                {isEdit
                  ? t('routes.admin.email.servers.edit.submit')
                  : t('routes.admin.email.servers.create.submit')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
